import type { CachedDocument } from "../../adapters/Firebase/Firebase";
import extractBirdsData, { BirdsCountingMode } from "../../components/BirdsData";
import extractDishwasherData from "../../components/DishwasherData";
import extractFruitsData, { FruitDeliveryStatus } from "../../components/FruitsData";
import extractGonogoData from "../../components/GonogoData";
import extractGrillData, { DeliveryStatus } from "../../components/GrillData";
import { combineMultigraphData, getTrialData } from "../../components/MultiObjectData";
import extractCleanSurfaceData from "../../components/Graphs/CleanSurface/CleanSurfaceData";

export type CsvValue = string | number | boolean | null | undefined;
export type CsvRow = Record<string, CsvValue>;
type Session = Pick<CachedDocument, "id" | "doc">;

// Shared keys mean shared definitions and units. Distinct denominators keep distinct keys.
const handColumns = ["left_hand_percentage", "right_hand_percentage"];
const feedbackColumns = ["left_feedback_count", "right_feedback_count"];
export const metricColumns: Record<string, string[]> = {
  multiobject: [
    "trial_count", "attempt_count", "total_reaction_time_seconds",
    "average_reaction_time_seconds", "error_count", "attempt_error_percentage", "clue_count",
    ...handColumns, "left_hand_error_count", "right_hand_error_count",
    "left_target_average_reaction_time_seconds", "right_target_average_reaction_time_seconds",
    ...feedbackColumns,
  ],
  gonogo: [
    "trial_count", "average_reaction_time_seconds", "commission_error_count",
    "omission_error_count", "error_count", "trial_error_percentage", "trial_success_percentage",
  ],
  fruits: [
    ...handColumns, "total_time_seconds", "average_time_per_picked_orange_seconds",
    "correct_orange_count", "green_oranges_taken_count", "ripe_oranges_wasted_count",
  ],
  birds: [
    "bird_counting_mode", "birds_presented_count", "bird_counting_error",
    "bird_counting_error_percentage", "left_bird_counting_error",
    "centre_bird_counting_error", "right_bird_counting_error", ...feedbackColumns,
  ],
  grill: [
    "correct_skewer_average_preparation_time_seconds", "correct_skewer_count",
    "corrected_skewer_count", "incorrect_skewer_count", "discarded_skewer_count",
    "timed_out_skewer_count", "raw_skewer_count", "burnt_skewer_count",
    "wrong_recipe_skewer_count", "wrong_tray_skewer_count", "wrong_order_skewer_count",
    "doorbell_hit_count", "doorbell_miss_count", "doorbell_false_alarm_count",
    "doorbell_correct_rejection_count",
  ],
  dishwasher: [
    "total_time_seconds", "dishwasher_item_count", "correctly_placed_item_count",
    "incorrectly_oriented_item_count", "misplaced_item_count", "misplaced_item_percentage",
    ...["mug", "fork", "dish"].flatMap(item => [
      item + "_correctly_placed_count", item + "_incorrectly_oriented_count",
      item + "_misplaced_count",
    ]),
  ],
  cleanSurface: [
    "total_time_seconds", ...["left", "right"].flatMap(side => [
      side + "_stain_count", side + "_cleaning_error_count", side + "_cleaning_error_percentage",
      side + "_cleaning_time_span_seconds", side + "_cleaning_average_interval_seconds",
    ]),
  ],
};

export function isCsvSessionSupported(session: Session): boolean {
  return session.doc?.processID !== "coffee" &&
    Object.prototype.hasOwnProperty.call(metricColumns, session.doc?.processType);
}

function divide(numerator: number, denominator: number): number | undefined {
  return denominator > 0 && Number.isFinite(numerator) ? numerator / denominator : undefined;
}

function percentage(numerator: number, denominator: number): number | undefined {
  const ratio = divide(numerator, denominator);
  return ratio === undefined ? undefined : ratio * 100;
}

function handMetrics(left: number, right: number): CsvRow {
  return {
    left_hand_percentage: percentage(left, left + right),
    right_hand_percentage: percentage(right, left + right),
  };
}

function feedbackMetrics(trials: any[]): CsvRow {
  const sum = (key: string) => trials.every(t => Number.isFinite(t.lateralFeedback?.[key]))
    ? trials.reduce((total, t) => total + t.lateralFeedback[key], 0) : undefined;
  return {
    left_feedback_count: sum("leftFeedbackCount"),
    right_feedback_count: sum("rightFeedbackCount"),
  };
}

// Embedded result objects and legacy subcollections both reach this boundary.
export function normalizeSessionBlocks(blocks: any): any[] {
  if (blocks == null) return [];
  return (Array.isArray(blocks) ? blocks : [blocks]).filter(block => block != null);
}

function sessionMetrics(type: string, blocks: any[], doc: any): CsvRow {
  if (!blocks.length) return {};
  const trials = () => blocks.flatMap(block => {
    if (!Array.isArray(block.trials)) throw new Error("Missing session trials");
    return block.trials;
  });

  switch (type) {
    case "multiobject": {
      const rawTrials = trials();
      const data = combineMultigraphData(rawTrials.map(getTrialData));
      return {
        trial_count: data.count,
        attempt_count: data.triesCount,
        total_reaction_time_seconds: data.reactionTime,
        average_reaction_time_seconds: divide(data.reactionTime, data.count),
        error_count: data.errors,
        attempt_error_percentage: percentage(data.errors, data.triesCount),
        clue_count: data.usedClues,
        ...handMetrics(data.handedness.left, data.handedness.right),
        left_hand_error_count: data.handedness.leftErrors,
        right_hand_error_count: data.handedness.rightErrors,
        left_target_average_reaction_time_seconds: divide(data.leftReactionTime, data.leftTriesCount),
        right_target_average_reaction_time_seconds: divide(data.rightReactionTime, data.rightTriesCount),
        ...feedbackMetrics(rawTrials),
      };
    }
    case "gonogo": {
      // All blocks contribute; the legacy visualizer currently reads only the first.
      const data = extractGonogoData({ trials: trials() });
      const errors = data.commisionErrors + data.omissionErrors;
      return {
        trial_count: data.count,
        average_reaction_time_seconds: divide(data.reactionTime, data.count),
        commission_error_count: data.commisionErrors,
        omission_error_count: data.omissionErrors,
        error_count: errors,
        trial_error_percentage: percentage(errors, data.count),
        trial_success_percentage: percentage(data.count - errors, data.count),
      };
    }
    case "fruits": {
      const deliveredFruits = blocks.flatMap(block => {
        if (!Array.isArray(block.deliveredFruits)) throw new Error("Missing fruit results");
        return block.deliveredFruits;
      });
      const data = extractFruitsData({ blocks: { deliveredFruits } });
      const picked = data.tries.filter(t => t.deliveryStatus !== FruitDeliveryStatus.NotDelivered);
      return {
        ...handMetrics(data.handedness.left, data.handedness.right),
        total_time_seconds: data.totalTime,
        average_time_per_picked_orange_seconds: divide(data.totalTime, picked.length),
        correct_orange_count: data.tries.filter(t => !t.isForbidden && t.deliveryStatus === FruitDeliveryStatus.Binned).length,
        green_oranges_taken_count: picked.filter(t => t.isForbidden).length,
        ripe_oranges_wasted_count: data.tries.filter(t => !t.isForbidden && t.deliveryStatus !== FruitDeliveryStatus.Binned).length,
      };
    }
    case "birds": {
      const rawTrials = trials().filter(t => t.counts !== null);
      // Orientation is not part of the export and must not be required to export older results.
      const data = extractBirdsData({ blocks: { trials: rawTrials.map(t => ({
        ...t, viewOrientation: { samples: [] },
      })) } });
      const errors = data.leftErrors + data.middleErrors + data.rightErrors;
      const modes = Array.from(new Set(rawTrials.map(t =>
        t.countingMode || (t.perTree ? BirdsCountingMode.PerTree : BirdsCountingMode.Together))));
      return {
        bird_counting_mode: modes.map(mode => ["together", "left_and_right", "per_tree"][mode]).join("; "),
        birds_presented_count: data.totalTries,
        bird_counting_error: errors,
        bird_counting_error_percentage: percentage(errors, data.totalTries),
        left_bird_counting_error: modes.some(m => m !== BirdsCountingMode.Together) ? data.leftErrors : undefined,
        centre_bird_counting_error: modes.length === 1 && modes[0] === BirdsCountingMode.PerTree ? data.middleErrors : undefined,
        right_bird_counting_error: modes.some(m => m !== BirdsCountingMode.Together) ? data.rightErrors : undefined,
        ...feedbackMetrics(rawTrials),
      };
    }
    case "grill": {
      const deliveredSkewers = blocks.flatMap(block => {
        if (!Array.isArray(block.deliveredSkewers)) throw new Error("Missing grill results");
        return block.deliveredSkewers;
      });
      const hasRings = blocks.every(block => Array.isArray(block.ringResults));
      const data = extractGrillData({
        modifiers: doc.modifiers ?? {},
        blocks: { deliveredSkewers, ringResults: blocks.flatMap(b => b.ringResults ?? []) },
      });
      const correct = data.deliveries.filter(d => d.status === DeliveryStatus.Correct);
      const countFlag = (flag: DeliveryStatus) => data.deliveries.filter(d => (d.status & flag) !== 0).length;
      return {
        correct_skewer_average_preparation_time_seconds: divide(correct.reduce((total, d) => total + d.time, 0), correct.length),
        correct_skewer_count: correct.length,
        corrected_skewer_count: data.deliveries.filter(d => d.status === DeliveryStatus.Ammended).length,
        // Intentionally includes corrected skewers, matching AverageGrillCard.
        incorrect_skewer_count: data.deliveries.filter(d => d.status !== DeliveryStatus.Correct &&
          (d.status & DeliveryStatus.TimedOut) === 0 && (d.status & DeliveryStatus.Discarded) === 0).length,
        discarded_skewer_count: countFlag(DeliveryStatus.Discarded),
        timed_out_skewer_count: countFlag(DeliveryStatus.TimedOut),
        raw_skewer_count: data.deliveries.filter(d => (d.status & DeliveryStatus.Raw) !== 0 &&
          (d.status & DeliveryStatus.TimedOut) === 0).length,
        burnt_skewer_count: countFlag(DeliveryStatus.Burnt),
        wrong_recipe_skewer_count: countFlag(DeliveryStatus.IncorrectRecipe),
        wrong_tray_skewer_count: countFlag(DeliveryStatus.IncorrectTray),
        wrong_order_skewer_count: countFlag(DeliveryStatus.IncorrectOrder),
        doorbell_hit_count: hasRings ? data.rings.filter(r => r.isTarget && r.answered).length : undefined,
        doorbell_miss_count: hasRings ? data.rings.filter(r => r.isTarget && !r.answered).length : undefined,
        doorbell_false_alarm_count: hasRings ? data.rings.filter(r => !r.isTarget && r.answered).length : undefined,
        doorbell_correct_rejection_count: hasRings ? data.rings.filter(r => !r.isTarget && !r.answered).length : undefined,
      };
    }
    case "dishwasher": {
      const results = blocks.map(block => extractDishwasherData({ blocks: block }));
      const row: CsvRow = { total_time_seconds: results.reduce((total, data) => total + data.totalTime, 0) };
      let correct = 0;
      let orientedIncorrectly = 0;
      let misplaced = 0;
      (["mugs", "forks", "dishes"] as const).forEach((key, index) => {
        const collections = results.map(data => data[key]);
        const items = collections.flatMap(data => data.items);
        const wellPlaced = items.filter(item => item.wellOriented).length;
        const badPlaced = items.length - wellPlaced;
        const notPlaced = collections.reduce((total, data) => total + data.missplaced, 0);
        const prefix = ["mug", "fork", "dish"][index];
        row[prefix + "_correctly_placed_count"] = wellPlaced;
        row[prefix + "_incorrectly_oriented_count"] = badPlaced;
        row[prefix + "_misplaced_count"] = notPlaced;
        correct += wellPlaced;
        orientedIncorrectly += badPlaced;
        misplaced += notPlaced;
      });
      return {
        ...row,
        dishwasher_item_count: correct + orientedIncorrectly + misplaced,
        correctly_placed_item_count: correct,
        incorrectly_oriented_item_count: orientedIncorrectly,
        misplaced_item_count: misplaced,
        misplaced_item_percentage: percentage(misplaced, correct + orientedIncorrectly + misplaced),
      };
    }
    case "cleanSurface": {
      const results = blocks.map(block => extractCleanSurfaceData({ blocks: block }));
      const row: CsvRow = { total_time_seconds: results.reduce((total, data) => total + data.totalTime, 0) };
      ["left", "right"].forEach(side => {
        const groups = results.map(data => data.cells.filter(cell =>
          side === "left" ? cell.coordinates.x <= 0.5 : cell.coordinates.x > 0.5)
          .sort((a, b) => a.completionTime - b.completionTime));
        const cells = groups.flat();
        const errors = cells.filter(cell => !cell.completed || cell.spongeColor !== cell.stainColor).length;
        const span = groups.reduce((total, group) => total +
          (group.length < 2 ? 0 : group[group.length - 1].completionTime - group[0].completionTime), 0);
        row[side + "_stain_count"] = cells.length;
        row[side + "_cleaning_error_count"] = errors;
        row[side + "_cleaning_error_percentage"] = percentage(errors, cells.length);
        row[side + "_cleaning_time_span_seconds"] = span;
        // Preserve the dashboard's stain-count denominator, including incomplete cells.
        row[side + "_cleaning_average_interval_seconds"] = divide(span, cells.length);
      });
      return row;
    }
    default: return {};
  }
}

export function sessionCsvRow(session: Session, rawBlocks: any, processName: string): CsvRow {
  const doc = session.doc;
  const date = typeof doc.timestamp?.seconds === "number"
    ? new Date(doc.timestamp.seconds * 1000) : undefined;
  const row: CsvRow = {
    session_date_utc: date && Number.isFinite(date.getTime())
      ? date.toISOString().slice(0, 16).replace(/-/g, " ").replace("T", " ") : undefined,
    process_name: processName || doc.processID,
  };
  Object.keys(doc.modifiers ?? {}).sort().forEach(key => {
    const value = doc.modifiers[key];
    row["setting." + key] = value !== null && typeof value === "object" ? JSON.stringify(value) : value;
  });
  // Declare every applicable column, even when the recorded values are unavailable.
  metricColumns[doc.processType].forEach(key => { row[key] = undefined; });
  return { ...row, ...sessionMetrics(doc.processType, normalizeSessionBlocks(rawBlocks), doc) };
}

export function escapeCsvValue(value: CsvValue): string {
  if (value == null || (typeof value === "number" && !Number.isFinite(value))) return "";
  let text = typeof value === "number" ? String(Number(value.toFixed(6))) : String(value);
  // Keep imported text from being interpreted as a spreadsheet formula.
  if (typeof value === "string" && /^\s*[=+\-@\t\r\n]/.test(text)) text = "'" + text;
  return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

export function serializeSessionCsv(rows: CsvRow[]): string {
  if (!rows.length) return "";
  const present = new Set(rows.flatMap(row => Object.keys(row)));
  const common = ["session_date_utc", "process_name"];
  const settings = Array.from(present).filter(key => key.startsWith("setting.")).sort();
  const metrics = Array.from(new Set(Object.values(metricColumns).flat())).filter(key => present.has(key));
  const headers = [...common, ...settings, ...metrics];
  const lines = [headers, ...rows.map(row => headers.map(key => row[key]))];
  return "\uFEFF" + lines.map(line => line.map(escapeCsvValue).join(",")).join("\r\n") + "\r\n";
}

export async function createSessionsCsv(
  sessions: CachedDocument[],
  translate: (key: string) => string,
  loadBlocks: (session: CachedDocument) => Promise<any>,
): Promise<string> {
  const rows = await Promise.all(sessions.filter(isCsvSessionSupported).map(async session =>
    sessionCsvRow(session, await loadBlocks(session), translate("[process_" + session.doc.processID + "_title]"))));
  return serializeSessionCsv(rows);
}

export function downloadSessionsCsv(csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "recognition-sessions-" + new Date().toISOString().slice(0, 10) + ".csv";
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
